import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { CurrentContextService } from '../../core/context/current-context.service';
import { CatalogOffering, CatalogService, CatalogWarehouse, InventoryRequisition } from './catalog.service';
import { isPermissionDeniedError } from './inventory-api-error';

@Component({
  standalone: true,
  selector: 'ain-inventory-requisitions',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, FormsModule, ButtonModule, InputTextModule, SelectModule, TableModule, AinCanAccessDirective],
  template: `
    <ain-page [title]="t('catalog.requisition.title')" [description]="t('catalog.requisition.description')">
      <ain-inventory-page-hint pageKey="requisitions" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.inventory.states.noPermission')" [message]="t('catalog.inventory.states.noPermissionMessage')" />
      } @else {
        <section class="form-card">
          <h3>{{ t('catalog.requisition.createTitle') }}</h3>
          <div class="form-grid">
            <label><span>{{ t('catalog.inventory.reference') }}</span><input pInputText [(ngModel)]="form.referenceNumber" /></label>
            <label><span>{{ t('catalog.inventory.warehouseId') }}</span><p-select [(ngModel)]="form.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" /></label>
            <label><span>{{ t('catalog.inventory.offeringId') }}</span><p-select [(ngModel)]="form.offeringId" [options]="offeringOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" /></label>
            <label><span>{{ t('catalog.requisition.requestedQty') }}</span><input pInputText type="number" min="0" step="0.001" [(ngModel)]="form.requestedQuantity" /></label>
            <label class="wide"><span>{{ t('catalog.requisition.notes') }}</span><input pInputText [(ngModel)]="form.notes" /></label>
          </div>
          <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.requisitions.create'" [label]="t('catalog.action.create')" (click)="create()"></button>
        </section>

        <section class="filters">
          <label><span>{{ t('columns.status') }}</span>
            <p-select [(ngModel)]="statusFilter" [options]="statusOptions" optionLabel="label" optionValue="value" showClear="true" appendTo="body" (ngModelChange)="applyFilters()" />
          </label>
        </section>

        <section class="list-card">
          <p-table
            [value]="requisitions()"
            [paginator]="true"
            [rows]="pageSize"
            [first]="tableFirst()"
            [totalRecords]="totalCount()"
            [lazy]="true"
            [loading]="tableLoading()"
            (onLazyLoad)="onLazyLoad($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.inventory.reference') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('catalog.inventory.warehouseId') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.referenceNumber }}</td>
                <td>{{ statusLabel(row.status) }}</td>
                <td>{{ warehouseLabel(row.warehouseId) }}</td>
                <td><a [routerLink]="['/inventory/requisitions', row.id]">{{ t('common.open') }}</a></td>
              </tr>
            </ng-template>
          </p-table>
        </section>
        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .form-card, .list-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 12px; background: #fff; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid label.wide { grid-column: 1 / -1; }
    .filters { margin-bottom: 12px; }
    .error { color: #b91c1c; }
  `]
})
export class InventoryRequisitionsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly tableLoading = signal(false);
  readonly permissionDenied = signal(false);
  readonly error = signal('');
  readonly requisitions = signal<InventoryRequisition[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly totalCount = signal(0);
  readonly tableFirst = signal(0);
  readonly pageSize = 15;
  pageNumber = 1;
  statusFilter: string | null = null;

  private loadInFlight = false;
  private lastLazyFirst = -1;

  get statusOptions() {
    return [
      { label: this.t('catalog.requisition.status.draft'), value: 'Draft' },
      { label: this.t('catalog.requisition.status.submitted'), value: 'Submitted' },
      { label: this.t('catalog.requisition.status.converted'), value: 'Converted' },
      { label: this.t('catalog.requisition.status.cancelled'), value: 'Cancelled' }
    ];
  }

  form = { referenceNumber: '', warehouseId: '', offeringId: '', requestedQuantity: 0, notes: '' };

  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));
  offeringOptions = () => this.offerings().filter(o => o.inventoryEnabled).map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  warehouseLabel = (id: string) => this.warehouses().find(w => w.id === id)?.code ?? id;
  statusLabel = (status: string) => {
    const key = `catalog.requisition.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  };

  t(key: string): string { return this.localization.translate(key); }

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.fetchRequisitions(false)
    );
    this.service.listOfferings().subscribe({ next: o => this.offerings.set(o) });
    this.service.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    this.loading.set(false);
  }

  create(): void {
    this.error.set('');
    if (!this.form.referenceNumber.trim() || !this.form.warehouseId || !this.form.offeringId || this.form.requestedQuantity <= 0) {
      this.error.set(this.t('catalog.requisition.requiredFields'));
      return;
    }
    this.service.createInventoryRequisition({
      warehouseId: this.form.warehouseId,
      referenceNumber: this.form.referenceNumber.trim(),
      notes: this.form.notes.trim() || null,
      lines: [{ offeringId: this.form.offeringId, requestedQuantity: this.form.requestedQuantity }]
    }).subscribe({
      next: req => void this.router.navigate(['/inventory/requisitions', req.id]),
      error: () => this.error.set(this.t('catalog.requisition.createFailed'))
    });
  }

  applyFilters(): void {
    this.pageNumber = 1;
    this.tableFirst.set(0);
    this.lastLazyFirst = -1;
    this.fetchRequisitions(true);
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    if (this.loadInFlight) {
      return;
    }

    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    if (first === this.lastLazyFirst && this.requisitions().length > 0) {
      return;
    }

    this.lastLazyFirst = first;
    this.tableFirst.set(first);
    this.pageNumber = Math.floor(first / rows) + 1;
    this.fetchRequisitions(true);
  }

  private fetchRequisitions(showTableLoading: boolean): void {
    if (this.loadInFlight) {
      return;
    }

    this.loadInFlight = true;
    if (showTableLoading) {
      this.tableLoading.set(true);
    }
    this.error.set('');
    this.permissionDenied.set(false);

    this.service.listInventoryRequisitions({
      status: this.statusFilter ?? undefined,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize
    }).subscribe({
      next: page => {
        this.requisitions.set(page.items ?? []);
        this.totalCount.set(page.totalCount ?? 0);
        this.tableLoading.set(false);
        this.loading.set(false);
        this.loadInFlight = false;
      },
      error: err => {
        if (isPermissionDeniedError(err)) {
          this.permissionDenied.set(true);
          this.error.set('');
        } else {
          this.error.set(this.t('catalog.requisition.loadFailed'));
        }
        this.tableLoading.set(false);
        this.loading.set(false);
        this.loadInFlight = false;
      }
    });
  }
}
